import { ReactNode, memo, useState, useEffect, useCallback } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { useRecurrenceSchedulerV2 } from '@/hooks/useRecurrenceSchedulerV2';
import { useBudgetSync } from '@/hooks/useBudgetSync';
import { useSidebar } from '@/contexts/SidebarContext';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { useCards } from '@/contexts/cards';
import { AppModal } from '@/components/ui/modal';
import { TransactionFormNew } from '@/components/forms/transaction/TransactionFormNew';

interface LayoutProps {
  children: ReactNode;
}

// Memoized Layout to prevent unnecessary re-renders during navigation
export const Layout = memo(({ children }: LayoutProps) => {
  const { isCollapsed } = useSidebar();
  const { cards } = useCards();
  const [showGlobalTransactionForm, setShowGlobalTransactionForm] = useState(false);
  
  // Initialize recurrence scheduler
  useRecurrenceSchedulerV2();

  // Initialize budget sync
  useBudgetSync();

  // Scroll to top on route change
  useScrollToTop();

  // Global listener for open-transaction-form (works on any page)
  const handleOpenForm = useCallback(() => {
    setShowGlobalTransactionForm(true);
  }, []);

  useEffect(() => {
    window.addEventListener('open-transaction-form', handleOpenForm);
    return () => window.removeEventListener('open-transaction-form', handleOpenForm);
  }, [handleOpenForm]);

  const transformedCards = cards.map(c => ({ id: c.id, name: c.name, bank: c.bank || '' }));
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        <Sidebar />
        <main 
          className={`flex-1 p-4 pt-20 pb-20 lg:p-6 lg:pt-20 lg:pb-6 transition-all duration-300 ${
            isCollapsed ? 'lg:ml-14' : 'lg:ml-64'
          }`}
        >
          <div className={`mx-auto page-gap animate-fade-in ${isCollapsed ? 'max-w-full' : 'max-w-7xl'}`}>
            {children}
          </div>
        </main>
      </div>
      <BottomNav />

      {showGlobalTransactionForm && (
        <AppModal
          open={showGlobalTransactionForm}
          onOpenChange={(open) => !open && setShowGlobalTransactionForm(false)}
          variant="form"
          size="lg"
        >
          <TransactionFormNew
            onClose={() => setShowGlobalTransactionForm(false)}
            onSubmit={() => setShowGlobalTransactionForm(false)}
            cards={transformedCards}
          />
        </AppModal>
      )}
    </div>
  );
});